import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { mainApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import HaciogluMapping from './HaciogluMapping';
import HaciogluMappingList from './HaciogluMappingList';
import HaciogluProductManagement from './HaciogluProductManagement';

// Interfaces
interface GtsProduct {
  id: string;
  name: string;
  stockCode: string;
  barcode?: string;
  salePrice: number;
  isQrMenu: boolean;
  isKiosk: boolean;
  categoryName?: string;
  brandName?: string;
  imageUrl?: string;
}

interface HaciogluProduct {
  id: number;
  stokKodu: number;
  stokAdi: string;
  urunGurubu?: string;
  aktif: boolean;
  imageUrl?: string;
  fiyat?: number;
  birim?: string;
  stokTuru?: string;
  odemeBirimi?: string;
}

interface ProductMapping {
  id: number;
  gtsProductId: string;
  gtsProductName: string;
  gtsProductImageUrl?: string;
  gtsStockCode: string;
  gtsCategoryName?: string;
  gtsBrandName?: string;
  haciogluStokKodu: number;
  haciogluStokAdi?: string;
  haciogluImageUrl?: string;
  haciogluUrunGurubu?: string;
  createdAt: string;
}

interface Suggestion {
  gtsProductId: string;
  gtsProductName: string;
  gtsStockCode: string;
  haciogluStokKodu: number;
  haciogluStokAdi: string;
  haciogluUrunGurubu?: string;
  similarity: number;
}

const HaciogluProductMapping: React.FC = () => {
  const { userDetails } = useAuth();
  

  // States
  const [loading, setLoading] = useState(false);
  const [gtsProducts, setGtsProducts] = useState<GtsProduct[]>([]);
  const [haciogluProducts, setHaciogluProducts] = useState<HaciogluProduct[]>([]);
  const [mappings, setMappings] = useState<ProductMapping[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [productManagementProducts, setProductManagementProducts] = useState<HaciogluProduct[]>([]);

  // Selection states
  const [selectedGtsProduct, setSelectedGtsProduct] = useState<GtsProduct | null>(null);
  const [selectedHaciogluProduct, setSelectedHaciogluProduct] = useState<HaciogluProduct | null>(null);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<ProductMapping | null>(null);
  const [suggestionsDialogOpen, setSuggestionsDialogOpen] = useState(false);

  // Snackbar states
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (userDetails?.companyId) {
      loadData();
    }
  }, [userDetails]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadGtsProducts(),
        loadHaciogluProducts(),
        loadMappings(),
        loadProductManagement()
      ]);
    } catch (error: any) {
      showSnackbar(error.message || 'An error occurred while loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadGtsProducts = async () => {
    try {
      const response = await mainApi.get(
        `/api/HaciogluProductMapping/gts-products/${userDetails?.companyId}`
      );
      if (response.data.success) {
        setGtsProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('GTS ürünleri yüklenemedi:', error);
      throw new Error('Failed to load GTS products');
    }
  };

  const loadHaciogluProducts = async () => {
    try {
      const response = await mainApi.get(
        `/api/HaciogluProductMapping/hacioglu-products/${userDetails?.companyId}`
      );
      if (response.data.success) {
        setHaciogluProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Hacıoğlu ürünleri yüklenemedi:', error);
      throw new Error('Failed to load Hacioglu products');
    }
  };

  const loadMappings = async () => {
    try {
      const response = await mainApi.get(
        `/api/HaciogluProductMapping/mappings/${userDetails?.companyId}`
      );
      if (response.data.success) {
        setMappings(response.data.mappings || []);
      }
    } catch (error) {
      console.error('Mappingler yüklenemedi:', error);
      throw new Error('Failed to load mappings');
    }
  };

  const loadProductManagement = async () => {
    try {
      const response = await mainApi.get(
        `/api/HaciogluProductMapping/hacioglu-products/${userDetails?.companyId}`
      );
      if (response.data.success) {
        setProductManagementProducts(response.data.products || []);
      }
    } catch (error) {
      console.error('Ürün yönetimi listesi yüklenemedi:', error);
    }
  };

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const response = await mainApi.get(
        `/api/HaciogluProductMapping/suggest/${userDetails?.companyId}`
      );
      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
        setSuggestionsDialogOpen(true);
        showSnackbar(`${response.data.suggestions.length} suggestions found`, 'success');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to load suggestions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = async () => {
    if (!selectedGtsProduct || !selectedHaciogluProduct) {
      showSnackbar('Please select a product from both sides', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await mainApi.post(
        `/api/HaciogluProductMapping/map`,
        {
          companyFirebaseId: userDetails?.companyId,
          gtsInventoryItemId: selectedGtsProduct.id,
          haciogluStokKodu: selectedHaciogluProduct.stokKodu
        }
      );

      if (response.data.success) {
        showSnackbar('Mapping created successfully', 'success');
        setSelectedGtsProduct(null);
        setSelectedHaciogluProduct(null);
        await loadMappings();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to create mapping', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = (mapping: ProductMapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteMapping = async () => {
    if (!mappingToDelete) return;

    setLoading(true);
    try {
      const response = await mainApi.post(
        `/api/HaciogluProductMapping/unmap`,
        {
          companyFirebaseId: userDetails?.companyId,
          gtsInventoryItemId: mappingToDelete.gtsProductId
        }
      );

      if (response.data.success) {
        showSnackbar('Mapping removed successfully', 'success');
        setDeleteDialogOpen(false);
        setMappingToDelete(null);
        await loadMappings();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to remove mapping', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApplySuggestion = async (suggestion: Suggestion) => {
    setLoading(true);
    try {
      const response = await mainApi.post(
        `/api/HaciogluProductMapping/map`,
        {
          companyFirebaseId: userDetails?.companyId,
          gtsInventoryItemId: suggestion.gtsProductId,
          haciogluStokKodu: suggestion.haciogluStokKodu
        }
      );

      if (response.data.success) {
        showSnackbar('Suggestion applied successfully', 'success');
        await loadMappings();
        setSuggestions(suggestions.filter(s => s.gtsProductId !== suggestion.gtsProductId));
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to apply suggestion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (product: { stokKodu: number; stokAdi: string; urunGurubu?: string; fiyat?: number }) => {
    if (!product.stokKodu || !product.stokAdi) {
      showSnackbar('Stock code and product name are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await mainApi.post(
        `/api/HaciogluProductMapping/hacioglu-product`,
        {
          companyFirebaseId: userDetails?.companyId,
          ...product
        }
      );

      if (response.data.success) {
        showSnackbar('Product added successfully', 'success');
        await Promise.all([loadProductManagement(), loadHaciogluProducts()]);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to add product', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async (id: number, updates: { stokAdi?: string; urunGurubu?: string; fiyat?: number }) => {
    setLoading(true);
    try {
      const response = await mainApi.post(
        `/api/HaciogluProductMapping/hacioglu-product/update`,
        {
          companyFirebaseId: userDetails?.companyId,
          id,
          ...updates
        }
      );

      if (response.data.success) {
        showSnackbar('Product updated successfully', 'success');
        await Promise.all([loadProductManagement(), loadHaciogluProducts()]);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to update product', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateProduct = async (product: HaciogluProduct) => {
    if (!window.confirm(`Are you sure you want to set "${product.stokAdi}" as passive?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await mainApi.post(
        `/api/HaciogluProductMapping/hacioglu-product/deactivate`,
        {
          companyFirebaseId: userDetails?.companyId,
          id: product.id
        }
      );

      if (response.data.success) {
        showSnackbar('Product set to passive successfully', 'success');
        await Promise.all([loadProductManagement(), loadHaciogluProducts()]);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to set product as passive', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getMappedProductIds = (): Set<string> => {
    return new Set(mappings.map(m => m.gtsProductId));
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Hacioglu Product Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Match GTS products with Hacioglu products
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AutoFixHighIcon />}
          onClick={loadSuggestions}
          disabled={loading}
        >
          Smart Suggestion
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Create Mapping" />
        <Tab label={`Existing Mappings (${mappings.length})`} />
        <Tab label="Product Management" />
      </Tabs>

      {/* Tab 1 - Eşleştirme */}
      {activeTab === 0 && (
        <HaciogluMapping
          loading={loading}
          gtsProducts={gtsProducts}
          haciogluProducts={haciogluProducts}
          mappedProductIds={getMappedProductIds()}
          selectedGtsProduct={selectedGtsProduct}
          selectedHaciogluProduct={selectedHaciogluProduct}
          onSelectGtsProduct={setSelectedGtsProduct}
          onSelectHaciogluProduct={setSelectedHaciogluProduct}
          onCreateMapping={handleCreateMapping}
        />
      )}

      {/* Tab 2 - Mevcut Eşleştirmeler */}
      {activeTab === 1 && (
        <HaciogluMappingList
          loading={loading}
          mappings={mappings}
          onDeleteMapping={handleDeleteMapping}
        />
      )}

      {/* Tab 3 - Ürün Yönetimi */}
      {activeTab === 2 && (
        <HaciogluProductManagement
          loading={loading}
          products={productManagementProducts}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onDeactivateProduct={handleDeactivateProduct}
        />
      )}

      {/* Silme Onay Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Mapping</DialogTitle>
        <DialogContent>
          <Typography>
            {mappingToDelete && (
              <>
                <strong>{mappingToDelete.gtsProductName}</strong> ve{' '}
                <strong>{mappingToDelete.haciogluStokAdi}</strong>. Are you sure you want to remove this mapping?
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteMapping} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Öneri Dialog */}
      <Dialog 
        open={suggestionsDialogOpen} 
        onClose={() => setSuggestionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Smart Mapping Suggestions</DialogTitle>
        <DialogContent>
          {suggestions.length === 0 ? (
            <Typography textAlign="center" py={3}>
              No suggestions found
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>GTS Product</TableCell>
                    <TableCell>Hacioglu Product</TableCell>
                    <TableCell>Hacioglu Code</TableCell>
                    <TableCell>Similarity</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((suggestion, index) => (
                    <TableRow key={index}>
                      <TableCell>{suggestion.gtsProductName}</TableCell>
                      <TableCell>{suggestion.haciogluStokAdi}</TableCell>
                      <TableCell>{suggestion.haciogluStokKodu}</TableCell>
                      <TableCell>
                        <Chip
                          label={`%${suggestion.similarity}`}
                          color={suggestion.similarity >= 90 ? 'success' : suggestion.similarity >= 70 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleApplySuggestion(suggestion)}
                        >
                          Apply
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HaciogluProductMapping;
